/**
 * Context — reference material curated for the AI layer (SPEC.md §8.13, D183), managed on
 * the Models page because it's part of the inference setup, not a capture or task feature.
 *
 * Same shape as `ModelConfigsCard`: list, add-above, edit-in-place, delete-with-confirm.
 * Scope (All / Area / Project) is picked when adding, and can be changed when editing (D222: an
 * entry the AI filed under the wrong scope is fixed where it is shown).
 */

import { useState } from "react";
import { api, type ContextEntry, type ContextInput, type ContextScopeType } from "../api";
import { useAreasAndProjects } from "../areasProjects";
import { useContextEntries } from "../contextEntries";
import { EditContextForm } from "./EditContextForm";
import { Pencil, Trash } from "./icons";
import { useAutoGrow } from "./useAutoGrow";

const SCOPES: { id: ContextScopeType; label: string }[] = [
  { id: "all", label: "All - personal context, read on every call" },
  { id: "area", label: "Area - domain knowledge scoped to one area" },
  { id: "project", label: "Project - scoped to one project" },
];

type AddFormState = {
  scopeType: ContextScopeType;
  scopeId: string;
  title: string;
  body: string;
};

const BLANK_ADD_FORM: AddFormState = { scopeType: "all", scopeId: "", title: "", body: "" };

function AddContextForm({ onSave }: { onSave: (body: ContextInput) => Promise<void> }) {
  const { areas, projects } = useAreasAndProjects();
  const [form, setForm] = useState<AddFormState>(BLANK_ADD_FORM);
  const [error, setError] = useState<string | null>(null);

  const needsScopeId = form.scopeType !== "all";
  const options =
    form.scopeType === "area" ? areas : form.scopeType === "project" ? projects : [];
  const canSave = form.title.trim() && form.body.trim() && (!needsScopeId || form.scopeId);
  const bodyRef = useAutoGrow(form.body);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!canSave) return;
    try {
      await onSave({
        scope_type: form.scopeType,
        scope_id: needsScopeId ? form.scopeId : null,
        title: form.title.trim(),
        body: form.body.trim(),
      });
      setForm(BLANK_ADD_FORM);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <form onSubmit={submit} style={{ display: "grid", gap: "0.6rem" }}>
      {error && <p className="error">{error}</p>}

      <div className="row wrap">
        {SCOPES.map((s) => (
          <label key={s.id} className="row small" title={s.label}>
            <input
              type="radio"
              name="scope_type"
              checked={form.scopeType === s.id}
              onChange={() => setForm((f) => ({ ...f, scopeType: s.id, scopeId: "" }))}
            />
            {s.label}
          </label>
        ))}
      </div>

      {needsScopeId && (
        <select
          className="input"
          aria-label={form.scopeType === "area" ? "Area" : "Project"}
          value={form.scopeId}
          onChange={(e) => setForm((f) => ({ ...f, scopeId: e.target.value }))}
        >
          <option value="">
            Select {form.scopeType === "area" ? "an area" : "a project"}…
          </option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      )}

      <input
        className="input"
        placeholder="Title - e.g. Job title, Preferred tools"
        aria-label="Title"
        value={form.title}
        onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
      />

      <textarea
        ref={bodyRef}
        className="input notes"
        placeholder="Body - the actual context the model should know"
        aria-label="Body"
        rows={3}
        value={form.body}
        onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
      />

      <div className="row wrap">
        <button className="btn primary small" type="submit" disabled={!canSave}>
          Add
        </button>
      </div>
    </form>
  );
}

export function ContextCard({
  refreshKey,
  onChange,
}: {
  refreshKey?: number;
  onChange?: () => void | Promise<void>;
}) {
  const { entries, error: loadError, reload } = useContextEntries(refreshKey);
  const { areas, projects } = useAreasAndProjects(refreshKey);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  function scopeLabel(entry: ContextEntry): string {
    if (entry.scope_type === "all") return "All";
    const source = entry.scope_type === "area" ? areas : projects;
    return source.find((o) => o.id === entry.scope_id)?.name ?? "-";
  }

  async function remove(entry: ContextEntry) {
    if (!window.confirm(`Delete "${entry.title}"? This cannot be undone.`)) return;
    try {
      await api.deleteContext(entry.id);
      if (editingId === entry.id) setEditingId(null);
      await reload();
      await onChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <>
      <section className="panel">
        <header>
          <h2>Add context</h2>
          <span className="muted small">
            Reference material the model always sees when relevant - never a fleeting thought
          </span>
        </header>

        {loadError && <p className="error">{loadError}</p>}

        <AddContextForm
          onSave={async (body) => {
            await api.createContext(body);
            await reload();
            await onChange?.();
          }}
        />
      </section>

      <section className="panel">
        <header>
          <h2>Context</h2>
          <span className="muted small">{entries.length} entries</span>
        </header>

        {error && <p className="error">{error}</p>}

        {entries.length === 0 ? (
          <p className="muted small">
            Nothing yet - add what the model should always know: who you are, how an area
            works, what a project needs.
          </p>
        ) : (
          <table className="tokens">
            <thead>
              <tr>
                <th>Title</th>
                <th>Scope</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) =>
                editingId === entry.id ? (
                  <tr key={entry.id}>
                    <td colSpan={3}>
                      <EditContextForm
                        entry={entry}
                        areas={areas}
                        projects={projects}
                        onSave={async (body) => {
                          await api.updateContext(entry.id, body);
                          setEditingId(null);
                          await reload();
                          await onChange?.();
                        }}
                        onCancel={() => setEditingId(null)}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr key={entry.id}>
                    <td>{entry.title}</td>
                    <td className="small muted">
                      {entry.scope_type === "all"
                        ? "All"
                        : `${entry.scope_type}: ${scopeLabel(entry)}`}
                    </td>
                    <td>
                      <button
                        className="btn ghost small"
                        onClick={() => setEditingId(entry.id)}
                        aria-label={`Edit: ${entry.title}`}
                        title="Edit"
                      >
                        <Pencil />
                      </button>{" "}
                      <button
                        className="btn ghost small danger"
                        onClick={() => void remove(entry)}
                        aria-label={`Delete: ${entry.title}`}
                        title="Delete"
                      >
                        <Trash />
                      </button>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
